import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { participantsApi } from '@/api/endpoints/participants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { formatDate } from '@/utils/format';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/utils/cn';

interface ParticipantsPageProps {
  readOnly?: boolean;
}

const SESSION_STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: 'Nie rozpoczął',
  IN_PROGRESS: 'W trakcie',
  COMPLETED: 'Zakończył',
};

const SESSION_STATUS_CLASS: Record<string, string> = {
  NOT_STARTED: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-green-100 text-green-700',
};

export const ParticipantsPage = ({ readOnly = false }: ParticipantsPageProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // W trybie organizatora (readOnly) zawsze pokazujemy dane sesji etapu 2
  const sessionStage = readOnly ? 2 : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['participants', { page, search: debouncedSearch, sessionStage }],
    queryFn: () =>
      participantsApi.getAll({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        sessionStage,
      }),
    refetchInterval: readOnly ? 30_000 : false,
  });

  const participants = data?.data ?? [];
  const meta = data?.meta;
  const showSessionCols = readOnly;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Uczestnicy</h2>
          <p className="mt-1 text-sm text-gray-500">
            {meta?.total ?? 0} uczestników zarejestrowanych
            {showSessionCols && ' · status sesji Etap 2'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 max-w-sm">
          <Input
            placeholder="Szukaj po imieniu, nazwisku lub emailu..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {search && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setPage(1); }}>
            Wyczyść
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista uczestników</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-10" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Imię i nazwisko</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Szkoła</TableHead>
                  {showSessionCols ? (
                    <>
                      <TableHead>Status testu (E2)</TableHead>
                      <TableHead>Email koordynatora</TableHead>
                    </>
                  ) : (
                    <TableHead>Zarejestrowano</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {participants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showSessionCols ? 5 : 4} className="py-10 text-center text-gray-500">
                      {search ? 'Brak wyników dla podanej frazy.' : readOnly ? (
                        'Brak uczestników.'
                      ) : (
                        <>
                          Brak uczestników.{' '}
                          <Link to="/import" className="text-primary-600 hover:underline">
                            Importuj plik JSON
                          </Link>
                          , aby dodać uczestników.
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  participants.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        {p.firstName} {p.lastName}
                      </TableCell>
                      <TableCell className="text-gray-600">{p.email}</TableCell>
                      <TableCell>
                        {p.school ? (
                          <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                            {p.school.name}
                          </span>
                        ) : '—'}
                      </TableCell>
                      {showSessionCols ? (
                        <>
                          <TableCell>
                            {p.sessionStatus ? (
                              <span
                                className={cn(
                                  'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
                                  SESSION_STATUS_CLASS[p.sessionStatus] ?? 'bg-gray-100 text-gray-600',
                                )}
                              >
                                {SESSION_STATUS_LABEL[p.sessionStatus] ?? p.sessionStatus}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Brak sesji</span>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {p.school?.coordinatorEmail ? (
                              <a
                                href={`mailto:${p.school.coordinatorEmail}`}
                                className="text-primary-600 hover:underline"
                              >
                                {p.school.coordinatorEmail}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell className="text-gray-500 text-sm">{formatDate(p.createdAt)}</TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Strona {meta.page} z {meta.totalPages}</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Poprzednia
            </Button>
            <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
              Następna
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
